import * as React from "react";
import { Card, CardContent } from "../../components/ui/card.tsx";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar.tsx";
import { Button } from "../../components/ui/button.tsx";
import { TrendingDown, ArrowRight, ChartPie } from "lucide-react";
import { useGetFailingStudents } from "../../api/performance/queries.js";

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-[300px] text-center p-6">
    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
      <ChartPie className="w-8 h-8 text-purple-600" />
    </div>
    <h3 className="text-lg font-medium mb-2">No Students at Risk</h3>
    <p className="text-sm text-gray-500 max-w-[250px]">
      All students are performing well above the minimum threshold
    </p>
  </div>
);

const LoadingState = () => (
  <div className="flex items-center justify-center h-[300px]">
    <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
  </div>
);

const getScoreColor = (score) => {
  if (score < 30) return "bg-red-500";
  if (score < 35) return "bg-orange-500";
  return "bg-yellow-500";
};

export default function FailingStudents() {
  const [isMobile, setIsMobile] = React.useState(false);
  const [expandedView, setExpandedView] = React.useState(false);
  const { data: failingStudents, isLoading } = useGetFailingStudents();

  // Check if we're on mobile device
  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);
  
  // Toggle between compact and expanded view on mobile
  const toggleExpandView = () => {
    if (isMobile) {
      setExpandedView(!expandedView);
    }
  };

  const isEmpty = !isLoading && (!failingStudents || failingStudents.length === 0);

  return (
    <Card className={`w-full ${isMobile && expandedView ? "h-auto" : "h-[360px]"}`}>
      <CardContent className="p-3 sm:p-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-2 sm:mb-4">
          <div className="flex items-center gap-2">
            <TrendingDown className="h-4 sm:h-5 w-4 sm:w-5 text-red-500" />
            <h3 className="text-sm sm:text-base font-semibold">Students Likely to Fail</h3>
          </div>
          <div className="flex space-x-2">
            {isMobile && !isEmpty && (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs h-7"
                onClick={toggleExpandView}
              >
                {expandedView ? "Collapse" : "Expand"}
              </Button>
            )}
            {!isEmpty && (
              <Button 
                variant="ghost" 
                size="sm"
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-900 h-7 sm:h-8"
              >
                View All
                <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>

        {isLoading ? <LoadingState /> : isEmpty ? <EmptyState /> : (
          <>
            <div className={`flex-1 space-y-1 sm:space-y-2 overflow-auto ${isMobile && !expandedView ? "max-h-[240px]" : ""}`}>
              {failingStudents.map((student, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-2 sm:p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <Avatar className="h-7 w-7 sm:h-9 sm:w-9 border-2 border-white">
                      <AvatarImage src={student.avatar} alt={student.name} />
                      <AvatarFallback>{student.name[0]}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <h4 className="font-medium text-xs sm:text-sm truncate">{student.name}</h4>
                      <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500">
                        <span className="truncate">Class {student.class}</span>
                        <span className="h-1 w-1 rounded-full bg-gray-300" />
                        <span className="truncate">{student.subject}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-4 pl-2 sm:pl-3">
                    <div className="flex flex-col items-end">
                      <div className="flex items-center gap-1 sm:gap-1.5">
                        <div className={`h-1.5 sm:h-2 w-1.5 sm:w-2 rounded-full ${getScoreColor(student.percentage)}`} />
                        <span className="font-medium text-xs sm:text-sm">{student.percentage}%</span>
                      </div>
                      <span className="text-[10px] sm:text-xs text-gray-500">Current Score</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="pt-2 sm:pt-3 mt-1 sm:mt-2 border-t">
              <div className={`flex ${isMobile ? "flex-col space-y-1" : "items-center justify-between"} text-[10px] sm:text-xs text-gray-500`}>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <div className="flex items-center gap-1 sm:gap-1.5">
                    <div className="h-1.5 sm:h-2 w-1.5 sm:w-2 rounded-full bg-red-500" />
                    <span>Critical (&lt;30%)</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-1.5">
                    <div className="h-1.5 sm:h-2 w-1.5 sm:w-2 rounded-full bg-orange-500" />
                    <span>At Risk (&lt;35%)</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-1.5">
                    <div className="h-1.5 sm:h-2 w-1.5 sm:w-2 rounded-full bg-yellow-500" />
                    <span>Warning (&lt;40%)</span>
                  </div>
                </div>
                <span>{failingStudents.length} students</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
